import { IDLE_ENTRY } from "./objectStore";

/**
 * Reads made during render become watches held after it.
 *
 * A component names the aliases it reads while it renders; after the render those names are reconciled into
 * watches on the session object store. `read(alias)` is therefore the whole of what a component does to get
 * an object — it never learns whether the value was cached, fetched or refetched.
 *
 *   render()               { this.objects.begin(); … this.objects.read(alias) … }
 *   componentDidMount()    { this.objects.commit(); }
 *   componentDidUpdate()   { this.objects.commit(); }
 *   componentWillUnmount() { this.objects.release(); }
 *
 * `getStore` returns the STORE, not the manager, so a component handed a client rather than the session
 * context can hold one too.
 */
export default class AliasWatch {
  constructor(getStore, onChange) {
    this.getStore = getStore;
    this.onChange = onChange;
    this.reading = new Set();      // aliases read this pass
    this.releases = new Map();     // alias -> release()
    this.store = null;
  }

  begin() {
    this.reading.clear();
  }

  read(alias) {
    if (!alias) return IDLE_ENTRY;
    this.reading.add(alias);
    const store = this.getStore();
    return store ? store.peek(alias) : IDLE_ENTRY;
  }

  commit() {
    const store = this.getStore() || null;
    if (store !== this.store) {
      this.release();
      this.store = store;
    }
    if (!store) return;

    for (const [alias, release] of [...this.releases]) {
      if (this.reading.has(alias)) continue;
      release();
      this.releases.delete(alias);
    }
    // A snapshot, like the release loop above it: `watch` and `touch` both reach back into a watcher
    // callback, and a set mutated while it is being iterated skips entries.
    for (const alias of [...this.reading]) {
      if (this.releases.has(alias)) {
        // Already watched — but a reset or a drop may have left it with no entry. This is what refills a
        // component after a session switch without the store guessing which old aliases still matter.
        store.touch(alias);
      } else {
        this.releases.set(alias, store.watch(alias, this.onChange));
      }
    }
  }

  release() {
    for (const release of this.releases.values()) release();
    this.releases.clear();
  }
}
